import axios from 'axios';
import apiConfig from '../config/api';
import { auth } from '../firebase';

const api = axios.create(apiConfig);

// Add request interceptor to include the Firebase ID token
api.interceptors.request.use(async (config) => {
  try {
    // Try Firebase auth first (for web platform)
    let user = auth.currentUser;
    
    // If Firebase auth is null, try to get user from AuthContext (for native platform fallback)
    if (!user) {
      const authContext = window.__TULIDU_AUTH_CONTEXT__;
      if (authContext && authContext.currentUser) {
        user = authContext.currentUser;
      }
    }
    
    if (user) {
      let token;
      if (typeof user.getIdToken === 'function') {
        // Firebase user - get Firebase ID token (don't force refresh to avoid quota limits)
        token = await user.getIdToken(false);
      } else {
        // Fallback user - use the stored Google ID token
        token = user.authentication?.idToken || user.idToken;
      }
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('🔐 No token available for API request');
      }
    } else {
      console.warn('🔐 No authenticated user found for API request');
    }
    return config;
  } catch (error) {
    console.error('🔐 Error getting auth token:', error);
    return config;
  }
});

// Add response interceptor to handle rate limits, retries, and authentication errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors (token expired/invalid)
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔐 401 Unauthorized - attempting token refresh');
      originalRequest._retry = true;

      try {
        // Try to get fresh token from Firebase auth
        let user = auth.currentUser;
        
        // If Firebase auth is null, try to get user from AuthContext (for native platform fallback)
        if (!user) {
          const authContext = window.__TULIDU_AUTH_CONTEXT__;
          if (authContext && authContext.currentUser) {
            user = authContext.currentUser;
          }
        }

        if (user && typeof user.getIdToken === 'function') {
          try {
            // Force refresh the token only when we get a 401 error
            const freshToken = await user.getIdToken(true);
            console.log('🔐 Got fresh token, retrying request');
            
            // Update the request with the fresh token
            originalRequest.headers.Authorization = `Bearer ${freshToken}`;
            
            // Retry the request with the fresh token
            return api(originalRequest);
          } catch (refreshError) {
            console.error('🔐 Token refresh failed:', refreshError);
            
            // Check if this is an Apple Sign-In user (no token refresh support)
            if (refreshError.message && refreshError.message.includes('Apple Sign-In token refresh not supported')) {
              console.log('🔐 Apple Sign-In token expired, user needs to sign in again');
              // Clear auth data and trigger re-authentication
              localStorage.removeItem('tulidu_auth');
              window.dispatchEvent(new CustomEvent('auth-token-expired'));
              return Promise.reject(error);
            }
            
            // For other refresh errors, clear auth data and trigger re-authentication
            localStorage.removeItem('tulidu_auth');
            window.dispatchEvent(new CustomEvent('auth-token-expired'));
            return Promise.reject(error);
          }
        } else {
          console.warn('🔐 No user available for token refresh');
          // Clear any invalid auth data
          localStorage.removeItem('tulidu_auth');
          
          // If we're on a native platform and have no user, try to trigger re-authentication
          if (window.Capacitor?.isNativePlatform()) {
            console.log('🔐 Triggering re-authentication on native platform');
            // Dispatch a custom event to trigger re-authentication in the UI
            window.dispatchEvent(new CustomEvent('auth-token-expired'));
          }
        }
      } catch (refreshError) {
        console.error('🔐 Token refresh failed:', refreshError);
        // Clear invalid auth data
        localStorage.removeItem('tulidu_auth');
        
        // If we're on a native platform, try to trigger re-authentication
        if (window.Capacitor?.isNativePlatform()) {
          console.log('🔐 Token refresh failed, triggering re-authentication on native platform');
          // Dispatch a custom event to trigger re-authentication in the UI
          window.dispatchEvent(new CustomEvent('auth-token-expired'));
        }
        
        // If refresh fails, the user needs to re-authenticate
        // We'll let the error propagate so the UI can handle it
      }
    }

    // If we get a 429 (rate limit) error and haven't retried yet
    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Get retry delay from response headers or use default (60 seconds)
      const retryAfter = error.response.headers['retry-after'] || 60;
      const retryDelay = parseInt(retryAfter, 10) * 1000;

      // Wait for the specified delay
      await new Promise(resolve => setTimeout(resolve, retryDelay));

      // Retry the request
      return api(originalRequest);
    }

    return Promise.reject(error);
  }
);

// User management
export const createOrUpdateUser = async (userData) => {
  console.log('🔐 createOrUpdateUser called with:', {
    uid: userData.uid,
    email: userData.email,
    hasIdToken: !!userData.idToken,
    idTokenLength: userData.idToken ? userData.idToken.length : 0
  });
  
  try {
    // If userData contains an idToken, add it to the Authorization header
    if (userData.idToken) {
      console.log('🔐 Using idToken in Authorization header');
      const response = await api.post('/api/users/login', userData, {
        headers: {
          Authorization: `Bearer ${userData.idToken}`
        }
      });
      console.log('🔐 createOrUpdateUser response:', response.data);
      return response.data;
    } else {
      console.log('🔐 No idToken, using default API call');
      const response = await api.post('/api/users/login', userData);
      console.log('🔐 createOrUpdateUser response:', response.data);
      return response.data;
    }
  } catch (error) {
    console.error('🔐 createOrUpdateUser error:', error);
    console.error('🔐 Error details:', error.message, error.response?.data);
    throw error;
  }
};

export const getUserInfo = async () => {
  const response = await api.get('/api/users/me');
  return response.data;
};

export const updatePublicName = async (publicName) => {
  const response = await api.put('/api/users/public-name', { public_name: publicName });
  return response.data;
};

export const deleteUserAccount = async () => {
  const response = await api.delete('/api/users/me');
  return response.data;
};

// Fetch notifications for the current user
export const getNotifications = async () => {
  const response = await api.get('/api/notifications');
  return response.data;
};

// Mark all notifications as read for the current user
export const markNotificationsRead = async () => {
  const response = await api.patch('/api/notifications/read');
  return response.data;
};

// Delete a notification by ID
export const deleteNotification = async (id) => {
  const response = await api.delete(`/api/notifications/${id}`);
  return response.data;
};

// Fetch all notifications (read and unread)
export const getAllNotifications = async () => {
  const response = await api.get('/api/notifications/all');
  return response.data;
};

export default api; 